/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useState } from 'react'
import { Col, Input as InputAnt, Row, AutoComplete } from 'antd'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import {
	BtnComponent,
	InputComponent,
	FormRowAdditionalElement,
	FormRowComponent,
	FormRowElement,
	UISizes,
	UITypes,
	Text,
	Indent,
	TextType,
	i18n,
} from 'LibNeo'
import { FieldNames } from 'Src/constants/FieldNames'
import { MODAL_NAME } from 'Src/constants/ModalName'
import { MIN_FIND_LENGTH, TIME_LIMIT } from 'Src/constants/Global'
import {
	ADDRESS_LABEL,
	ADDRESS_TYPE,
	DATA_FIELDS_WITH_TYPE,
	FIELDS,
	mappingShortLongList,
	TYPE_REQUEST_ADDRESS,
} from 'Src/constants/Address.constants'
import { getStringSource } from 'Helpers'
import { AddressEditFiasComponent } from 'LibNeo/components/Composite/AddressComponent/AddressEditFias.component'
import { AddressSuggest, AddressSuggestResult } from 'Src/models'
import {
	FinalPlaceAlert,
	FormBlock,
	AddressLabel,
	AutoCompleteStyled,
	AddressText,
	Icon,
} from 'LibNeo/components/Composite/AddressComponent/AdressEditStyled'
import { useAppDispatch } from 'Src/hooks/useAppDispatch'
import { dadataSlice, modalsSlice } from 'Modules'
import { AddressEditComponent } from './AddressEdit.component'

interface ComponentProps {
	form: WrappedFormUtils
	fieldName: FieldNames | string
	disabled?: boolean
	disabledEdit?: boolean
	placeholder?: string
	placeholderCode?: string
	// TODO check types
	defaultAddress?: AddressSuggest
	currentAddress?: AddressSuggest
	fieldOptions?: GetFieldDecoratorOptions
	onChange?: (value: string) => void
	onSelect?: (field: string, address: AddressSuggest) => void
	origin?: string
	tooltip?: string
	isFias?: boolean // change url for request address to dadata
	hideBlockFias?: boolean
	hideCompareAddressTitle?: boolean
	hideBlockEdit?: boolean
	fieldLabel?: ADDRESS_LABEL
	labelCode?: string
	errorText?: string
	isJuridical?: boolean
	hasFormRow?: boolean
	fieldTooltip?: string
	typeAddress?: ADDRESS_TYPE
	helpText?: string
}

const actions = {
	getAddressDadata: dadataSlice.actions.getAddress,
	showModal: modalsSlice.actions.showModal,
}

export const AddressComponent: FC<ComponentProps> = ({
	form,
	fieldName,
	disabled,
	disabledEdit,
	placeholder,
	placeholderCode,
	defaultAddress,
	currentAddress,
	fieldOptions,
	onChange,
	onSelect,
	origin,
	tooltip,
	isFias,
	hideBlockFias,
	hideCompareAddressTitle,
	hideBlockEdit,
	errorText,
	fieldLabel = ADDRESS_LABEL.ADDRESS,
	labelCode,
	isJuridical,
	hasFormRow,
	fieldTooltip,
	typeAddress = ADDRESS_TYPE.DEFAULT,
	helpText,
}) => {
	const { getAddressDadata, showModal } = useAppDispatch(actions)
	const [value, setValue] = useState<string | null>(null)
	const [isSearch, setIsSearch] = useState(false)
	const [query, setQuery] = useState<string | null>(null)
	const [findQuery, setFindQuery] = useState<string | null>(null)
	const [hasError, setHasError] = useState(false)
	const [addresses, setAddresses] = useState<AddressSuggestResult>([])

	const { getFieldError, setFields, getFieldValue } = form
	const DATA_FIELDS = DATA_FIELDS_WITH_TYPE(typeAddress)

	useEffect(() => {
		setValue(currentAddress?.result)
	}, [currentAddress?.result])

	useEffect(() => {
		setValue(defaultAddress?.result)
	}, [defaultAddress?.result])

	const errors = getFieldError(fieldName)
	useEffect(() => {
		if (errors) setHasError(true)
		else setHasError(false)
	}, [value, JSON.stringify(errors)])

	const onSelectAddress = (_value: string) => {
		const callback = (res: AddressSuggestResult) => {
			const findAddress = Array.isArray(res)
				? res?.find((address) => address?.result === _value)
				: res

			if (findAddress) {
				FIELDS.forEach((field) => {
					const dataField = DATA_FIELDS[field]
					const fieldTypeShort =
						'field_type_short' in dataField ? dataField.field_type_short : undefined
					if (fieldTypeShort)
						findAddress[dataField.field_type] =
							mappingShortLongList?.[findAddress[fieldTypeShort]] || findAddress[fieldTypeShort]
				})
				if (findAddress.block && !findAddress.block_type) {
					findAddress.block_type = 'к'
					findAddress.block_type_full = 'корпус'
				}
				findAddress.result = getStringSource(findAddress, typeAddress)
				setFields({ [fieldName]: { value: findAddress, touched: true } })
				setValue(findAddress?.result)
				if (onSelect) onSelect(fieldName, findAddress)
			}
		}
		if (isFias && _value?.length > MIN_FIND_LENGTH)
			getAddressDadata({
				params: {
					query: _value,
					origin,
				},
				typeRequest: TYPE_REQUEST_ADDRESS.FIAS_WITH_OKTMO,
				callback,
			})
		else callback(addresses)
	}

	const onSearch = (_query: string) => {
		setQuery(_query)
		if (!isSearch && _query?.length > MIN_FIND_LENGTH && _query !== findQuery) {
			setFindQuery(_query)
			getAddressDadata({
				params: { query: _query, origin },
				typeRequest: isFias ? TYPE_REQUEST_ADDRESS.FIAS : TYPE_REQUEST_ADDRESS.ADDRESS,
				callback: (res: AddressSuggestResult) => setAddresses(res),
			})
			setIsSearch(true)
			setTimeout(() => {
				setIsSearch(false)
			}, TIME_LIMIT)
		}
	}

	useEffect(() => {
		if (!isSearch && query) onSearch(query)
	}, [isSearch])

	const onChangeAddress = (_query: string) => {
		setValue(_query)
		setHasError(false)
		if (onChange) onChange(_query)
	}

	const onBlurAddress = () => setValue(getFieldValue(fieldName)?.result)

	const saveEditAddress = (address: AddressSuggest) => {
		const newAddress: AddressSuggest = { ...getFieldValue(fieldName), ...address }
		setFields({ [fieldName]: { value: newAddress, touched: true } })
		setValue(newAddress?.result)
		if (onSelect) onSelect(fieldName, newAddress)
	}

	const onClickEditAddress = () =>
		showModal({
			Component: (props): JSX.Element => (
				<AddressEditComponent
					{...props}
					saveEditAddress={saveEditAddress}
					initialData={getFieldValue(fieldName)}
					disabled={disabled || disabledEdit}
					typeAddress={typeAddress}
				/>
			),
			key: MODAL_NAME.EDIT_ADDRESS_RBIDOS_REQUEST,
			width: '80%',
		})
	const pHolder = i18n(placeholder || placeholderCode || 'cabinetPro.address')
	const getDadataItemsResult = (addresses) => {
		return Array.isArray(addresses)
			? addresses.map((address) => (
					<AutoComplete.Option
						style={{
							whiteSpace: 'initial',
							textOverflow: 'initial',
						}}
						key={address?.result}
						value={address?.result}
					>
						{address?.result}
					</AutoComplete.Option>
			  ))
			: [
					<AutoComplete.Option
						style={{
							whiteSpace: 'initial',
							textOverflow: 'initial',
						}}
						key={addresses?.result}
						value={addresses?.result}
					>
						{addresses?.result}
					</AutoComplete.Option>,
			  ]
	}
	return (
		<div>
			<div>
				<Row gutter={16}>
					<Col span={24}>
						{Boolean(labelCode || tooltip || !hideBlockEdit) && (
							<AddressLabel>
								{Boolean(labelCode) && <AddressText>{i18n(labelCode)}</AddressText>}
								{Boolean(tooltip) && (
									<Icon tooltip={tooltip} name="help" type={UITypes.PASSIVE} size={UISizes.SMALL} />
								)}
							</AddressLabel>
						)}
					</Col>
				</Row>
				<Row gutter={16}>
					<Col span={24}>
						{!hideCompareAddressTitle && (
							<FormBlock withSpacing bottom={12}>
								<FinalPlaceAlert
									type={UITypes.INFO}
									icon="location-pos"
									title={getStringSource(getFieldValue(fieldName), typeAddress) || 'Нет данных'}
								/>
							</FormBlock>
						)}
					</Col>
				</Row>
				{/* TODO Удалить проверку и вторую часть кода, когда удалят или поменяют дизайн страницы
				Адреса очной идентификации */}
				{isFias || hasFormRow ? (
					<FormRowComponent label={fieldLabel} tooltip={fieldTooltip}>
						<FormRowElement>
							<AutoCompleteStyled
								isError={hasError}
								value={typeof value === 'string' ? value : ''}
								disabled={disabled}
								dataSource={getDadataItemsResult(addresses)}
								onChange={onChangeAddress}
								onSelect={onSelectAddress}
								onSearch={onSearch}
								onBlur={onBlurAddress}
								filterOption={false}
							>
								<InputAnt.TextArea autoSize={{ minRows: 1 }} placeholder={pHolder.toString()} />
							</AutoCompleteStyled>
							<InputComponent
								placeholder="cabinetPro.address"
								form={form}
								fieldName={fieldName}
								fieldOptions={{ ...fieldOptions, ...{ initialValue: defaultAddress } }}
								type="hidden"
							/>
							{helpText && (
								<Indent top={6}>
									<Text type={TextType.CAPTION}>{helpText}</Text>
								</Indent>
							)}
							{hasError && <span>{errorText}</span>}
						</FormRowElement>
						<FormRowAdditionalElement>
							<BtnComponent
								icon="location-pos"
								type={UITypes.PASSIVE}
								outline
								onClick={onClickEditAddress}
								disabled={disabledEdit}
							/>
						</FormRowAdditionalElement>
					</FormRowComponent>
				) : (
					<Row gutter={[32, 0]}>
						<Col span={22}>
							<AutoCompleteStyled
								isError={hasError}
								value={value}
								disabled={disabled}
								dataSource={getDadataItemsResult(addresses)}
								onChange={onChangeAddress}
								onSelect={onSelectAddress}
								onSearch={onSearch}
								onBlur={onBlurAddress}
								filterOption={false}
							>
								<InputAnt.TextArea autoSize={{ minRows: 1 }} placeholder={pHolder.toString()} />
							</AutoCompleteStyled>
							<InputComponent
								placeholder={pHolder}
								form={form}
								fieldName={fieldName}
								fieldOptions={{ ...fieldOptions, ...{ initialValue: defaultAddress } }}
								type="hidden"
							/>
							{hasError && <span>{errorText}</span>}
						</Col>
						<Col span={2}>
							<BtnComponent
								icon="location-pos"
								type={UITypes.PASSIVE}
								outline
								onClick={onClickEditAddress}
								disabled={disabledEdit}
							/>
						</Col>
					</Row>
				)}
			</div>
			{isFias && !hideBlockFias && (
				<AddressEditFiasComponent
					saveEditAddress={saveEditAddress}
					initialData={getFieldValue(fieldName)}
					disabled={disabledEdit}
					isJuridical={isJuridical}
					typeAddress={typeAddress}
				/>
			)}
		</div>
	)
}
